import { kAnalyticsSessionParamsKey } from '../../analytics/sessionParams';

declare global {
  interface Window {
    analytics?: {
      load: (key: string) => void;
      page: (screen: string, params: unknown) => void;
      identify?: (uid: string, traits?: unknown, options?: unknown) => void;
      track?: (event: string, properties?: unknown, options?: unknown) => void;
      debug: (value: boolean) => void;
      reset: () => void;
    };
  }
}

const kAdsParams = [
  'gclid',
  'dclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'twclid',
  'clid_src',
  'msclkid',
  'ttclid',
];

// Marketing params held across the signup funnel exactly like `utm_*`, but
// not utm-prefixed and not ads click-ids. `onboarding_coupon` rides in on the
// landing URL (e.g. `/pricing-plans?utm_…&onboarding_coupon=x`); the signup
// funnel forwards it hop-to-hop in the URL until `/signup/finalize` persists
// it onto `privateProfiles.onboardingMarketingCapture` for the plan picker's
// promo — holding it here keeps it recoverable for the session alongside the
// utm_* set.
//
// `offer` is the opaque claim token an emailed coupon-campaign CTA carries
// (`/pricing-plans?offer=<token>`). The email is usually opened signed out or
// on another device, so the token has to outlive the login round trip the same
// way; the pricing scene reads it back with `getAnalyticsSessionCampaignOffer`.
export const CAMPAIGN_OFFER_PARAM = 'offer';
const kHeldMarketingParams = ['onboarding_coupon', CAMPAIGN_OFFER_PARAM];

export function captureAnalyticsSessionParams() {
  const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
  const paramsFromStorage = s ? JSON.parse(s) : {};

  const url = new URL(location.href);

  const additionalParams: Record<string, string> = {};

  // Keep the FIRST value of each `utm_*` key. A duplicated param
  // (`?utm_regintent=a&utm_regintent=b`) yields both via `.entries()`, and a
  // plain `Object.fromEntries` would keep the LAST (`b`) — disagreeing with
  // every other reader here (`URLSearchParams.get()`, the server's first-value
  // normalize, the /signup URL de-dupe), which all keep the first. Use `.get()`
  // per unique key so this stays consistent.
  const utmParamsFromUrl: Record<string, string> = {};
  for (const key of new Set(url.searchParams.keys())) {
    if (!key.startsWith('utm_')) continue;
    const value = url.searchParams.get(key);
    if (typeof value === 'string') utmParamsFromUrl[key] = value;
  }

  for (const key of [...kAdsParams, ...kHeldMarketingParams]) {
    if (
      url.searchParams.has(key) &&
      typeof url.searchParams.get(key) === 'string'
    ) {
      additionalParams[key] = url.searchParams.get(key) as string;
    }
  }

  const params = {
    ...paramsFromStorage,
    ...utmParamsFromUrl,
    ...additionalParams,
  };

  sessionStorage.setItem(kAnalyticsSessionParamsKey, JSON.stringify(params));
}

/**
 * Record a campaign param the ARRIVAL did not carry, so the rest of the funnel
 * reads it exactly as it reads a real one.
 *
 * One caller: `public_config.signup_fallback.onboarding_path`, the configured
 * default for a signup that carried no campaign at all. That value has to reach
 * the SAME bag a real `?utm_onboarding_path=` lands in, because the readers
 * downstream are synchronous and know nothing about Firestore — the
 * post-payment redirect is `getOnboardingPathFromUrl()`, which looks here and
 * then at the current URL, long after both the config and the signup form are
 * out of scope.
 *
 * ⚠️ SEEDS, never overwrites. A value already in the bag came from a real
 * campaign link and outranks any configured default — the config moves the
 * floor, it never takes a vote away from marketing. Same precedence the
 * business type follows in `signupBusinessType`.
 *
 * Silent on a blank value and on a `sessionStorage` that throws (private
 * windows, blocked site data): a missing default is the state that predates
 * this config, so failing to seed one can never be worse than not having it.
 */
export function seedAnalyticsSessionParam(
  key: string,
  value: string,
  /**
   * Replace a value already in the bag. Only for a value the caller has
   * already judged UNUSABLE — an `utm_onboarding_path` the system does not
   * recognise, which the configured default replaces. Never for a real one.
   */
  { overwrite = false }: { overwrite?: boolean } = {},
): void {
  if (typeof window === 'undefined' || !value) return;
  try {
    const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
    const params = (s ? JSON.parse(s) : {}) as Record<string, string>;
    if (params[key] && !overwrite) return;
    sessionStorage.setItem(
      kAnalyticsSessionParamsKey,
      JSON.stringify({ ...params, [key]: value }),
    );
  } catch {
    // no-op — see the note above
  }
}

/**
 * Params whose NAME is a contract with a backend, not an analytics label.
 *
 * `stripePrefix` renames `utm_x` to `x` for the analytics sinks, which is
 * right for everything that is only ever read as marketing attribution. It is
 * wrong for a param a backend matches on by name: the creator account create
 * passes `stripePrefix: true`, and the agency tagging in
 * `popstore-creator-api.controller.ts` reads `utmParameters.utm_agency` by
 * that exact spelling. Stripping it sent `agency`, so every `?utm_agency=`
 * signup onboarded successfully and tagged nobody, with no error anywhere —
 * the backend logged "no_utm_value" as though the link had carried nothing.
 */
const PREFIXED_CONTRACT_PARAMS = new Set(['utm_agency']);

export function getAnalyticsSessionCampaignParams(options?: {
  stripePrefix?: boolean;
}): Record<string, string> {
  const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
  const params = s ? JSON.parse(s) : {};

  return Object.fromEntries(
    Object.entries(params as Record<string, string>)
      .filter(([key]) => key.startsWith('utm_'))
      .map(([key, value]) => [
        options?.stripePrefix && !PREFIXED_CONTRACT_PARAMS.has(key)
          ? key.slice(4)
          : key,
        value,
      ]),
  );
}

/**
 * The signup coupon (`onboarding_coupon`) held by
 * `captureAnalyticsSessionParams` at app entry, if any. Read by the platform
 * subscription checkout to auto-apply the promo the creator arrived with.
 */
export function getAnalyticsSessionOnboardingCoupon(): string | undefined {
  const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
  const params = s ? (JSON.parse(s) as Record<string, string>) : {};
  const coupon = params.onboarding_coupon?.trim();
  return coupon ? coupon : undefined;
}

/**
 * The emailed campaign offer's claim token (`offer`) held by
 * `captureAnalyticsSessionParams` at app entry, if any.
 */
export function getAnalyticsSessionCampaignOffer(): string | undefined {
  const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
  const params = s ? (JSON.parse(s) as Record<string, string>) : {};
  const token = params[CAMPAIGN_OFFER_PARAM]?.trim();
  return token ? token : undefined;
}

export function getAnalyticsSessionAdsParams(): Record<string, string> {
  const s = sessionStorage.getItem(kAnalyticsSessionParamsKey);
  const params = s ? JSON.parse(s) : {};

  return Object.fromEntries(
    Object.entries(params as Record<string, string>).filter(([key]) =>
      kAdsParams.includes(key),
    ),
  );
}
